import React, { useEffect, useMemo, useState } from "react";
import api, { SettingsAPI } from "@/api/client";
import { Input } from "@/components/ui/input.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.jsx";
import { Checkbox } from "@/components/ui/checkbox.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table.jsx";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button.jsx";
import { ArrowUp, ArrowDown, Truck, CornerDownLeft, Euro, Trash2, Download } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuth } from "@/context/AuthContext.jsx";

const DEFAULT_COLUMNS = ["seriennummer", "beschreibung", "artikelnummer", "kunde", "status", "mietpreis_typ", "mietpreis_betrag", "hersteller", "farbe_ral", "baujahr", "cad_nummer"];

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  
  // Filter-States mit Benutzer-spezifischer Persistenz
  const [status, setStatus] = useState("all");
  const [activeFilters, setActiveFilters] = useState([]);
  const [q, setQ] = useState("");
  
  const debouncedQ = useDebounce(q, 800); // Debounce search input (optimized: 800ms)
  const [includeTimeline, setIncludeTimeline] = useState(() => {
    const saved = localStorage.getItem("bc_include_timeline");
    return saved ? JSON.parse(saved) : false; // Standardmäßig DEAKTIVIERT
  });
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem("bc_columns");
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem("bc_items_per_page");
    return saved ? parseInt(saved) : 50; // Optimized: Reduced from 100 to 50 for faster initial load
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [kpiStats, setKpiStats] = useState(null); // KPI-Statistiken für ALLE gefilterten Items
  
  // Speichere Filter bei Änderung (pro Benutzer)
  // Speichere Filter nur NACH Initialisierung (verhindert Überschreiben beim ersten Laden)
  useEffect(() => {
    if (user?.id && filtersInitialized) {
      localStorage.setItem(`bc_status_${user.id}`, status);
    }
  }, [status, user?.id, filtersInitialized]);
  
  useEffect(() => {
    if (user?.id && filtersInitialized) {
      localStorage.setItem(`bc_activeFilters_${user.id}`, JSON.stringify(activeFilters));
    }
  }, [activeFilters, user?.id, filtersInitialized]);
  
  useEffect(() => {
    if (user?.id && filtersInitialized) {
      localStorage.setItem(`bc_search_${user.id}`, q);
    }
  }, [q, user?.id, filtersInitialized]);
  
  // Lade gespeicherte Filter wenn sich der Benutzer ändert
  useEffect(() => {
    if (user?.id) {
      const savedStatus = localStorage.getItem(`bc_status_${user.id}`);
      const savedFilters = localStorage.getItem(`bc_activeFilters_${user.id}`);
      const savedSearch = localStorage.getItem(`bc_search_${user.id}`);
      const savedSort = localStorage.getItem(`bc_sort_${user.id}`);
      
      if (savedStatus) setStatus(savedStatus);
      if (savedFilters) {
        try {
          setActiveFilters(JSON.parse(savedFilters));
        } catch (e) {}
      }
      if (savedSearch) setQ(savedSearch);
      if (savedSort) {
        try {
          setSort(JSON.parse(savedSort));
        } catch (e) {}
      }
      
      // Markiere Filter als initialisiert NACH dem Laden
      setFiltersInitialized(true);
    }
  }, [user?.id]);
  
  useEffect(() => {
    localStorage.setItem("bc_include_timeline", JSON.stringify(includeTimeline));
  }, [includeTimeline]);
  
  useEffect(() => {
    localStorage.setItem("bc_items_per_page", itemsPerPage.toString());
  }, [itemsPerPage]);
  
  const [sort, setSort] = useState({ key: "seriennummer", dir: "asc" });
  
  // Speichere Sortierung bei Änderung
  useEffect(() => {
    if (user?.id && filtersInitialized) {
      localStorage.setItem(`bc_sort_${user.id}`, JSON.stringify(sort));
    }
  }, [sort, user?.id, filtersInitialized]);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCreateAnlageDialog, setShowCreateAnlageDialog] = useState(false);
  const [showColumnsDialog, setShowColumnsDialog] = useState(false);
  const [nextSerial, setNextSerial] = useState(null);
  const [nextAnlagenSerial, setNextAnlagenSerial] = useState(null);
  const [newHersteller, setNewHersteller] = useState("Pirschel");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("none");
  const [batchAnzahl, setBatchAnzahl] = useState(1);
  const [batchBeschreibung, setBatchBeschreibung] = useState("");
  const [batchBaujahrMonat, setBatchBaujahrMonat] = useState("");
  const [batchBaujahrJahr, setBatchBaujahrJahr] = useState(new Date().getFullYear().toString());
  const [batchFarbeRal, setBatchFarbeRal] = useState("");
  const [batchAnschaffungspreis, setBatchAnschaffungspreis] = useState("");
  const [batchCreatedIds, setBatchCreatedIds] = useState(null);
  const [batchCreating, setBatchCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams();
    // "used_online" und "checkout_ready" werden Frontend-seitig gefiltert, nicht ans Backend senden
    if (status && status !== "all" && status !== "used_online" && status !== "checkout_ready") params.set("status", status);
    if (debouncedQ) {
      params.set("q", debouncedQ);
      // WORKAROUND: Bei Suche immer include_timeline=true nutzen (Backend-Bug mit aktueller_kunde bei include_timeline=false)
      // User kann Toggle nutzen um zwischen "nur aktuelle" und "historische" zu wechseln
      // Aber bei Suche immer volle Timeline laden damit Backend aktuellen Kunden ermitteln kann
      params.set("include_timeline", debouncedQ ? "true" : includeTimeline.toString());
    }
    
    // KPI-Filter: Wenn NUR EIN Filter aktiv ist, sende an Backend für korrekte Pagination
    if (activeFilters.length === 1) {
      const filter = activeFilters[0];
      // Normale Item-Type Filter (becken, anlage)
      if (filter.type !== 'checkout') {
        params.set("item_type", filter.type);
        params.set("status", filter.status);
      }
      // Checkout-Filter
      else if (filter.type === 'checkout') {
        if (filter.status === 'in_process') params.set("status", "checkout_in_process");
        if (filter.status === 'ready') params.set("status", "checkout_ready");
      }
    }
    
    // WICHTIG: Bei aktiver Suche IMMER Backend-Suche verwenden
    // Bei Filtern ohne Suche: Lade alle Items für Client-seitige Pagination
    const hasActiveSearch = debouncedQ && debouncedQ.length > 0;
    const needsClientFiltering = !hasActiveSearch && (activeFilters.length >= 1 || status === "used_online" || status === "checkout_in_process" || status === "checkout_ready" || (status === "all" && activeFilters.length === 0));
    
    if (needsClientFiltering) {
      // Lade ALLE Items für Client-seitige Pagination (nur wenn KEINE Suche aktiv)
      params.set("limit", "9999");
    } else if (hasActiveSearch) {
      // Bei Suche: Alle Ergebnisse laden für client-seitige Paginierung
      params.set("limit", "9999");
    } else {
      // Backend-Pagination bei Suche ODER bei leerem Dashboard
      params.set("page", currentPage.toString());
      params.set("limit", itemsPerPage.toString());
      
      if (sort.key) {
        params.set("sort_by", sort.key);
        params.set("sort_dir", sort.dir);
      }
    }
    
    setLoading(true);
    api
      .get(`/items${params.toString() ? `?${params.toString()}` : ""}`)
      .then((res) => {
        const itemsData = res.data.items || res.data;
        console.log('[Dashboard] API Response:', { 
          total: res.data.total, 
          itemsCount: itemsData.length,
          firstItem: itemsData[0]?.seriennummer,
          debouncedQ 
        });
        setItems(itemsData);
        setTotalItems(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
        setKpiStats(res.data.kpi_stats || null);
      })
      .finally(() => setLoading(false));
  }, [status, debouncedQ, includeTimeline, currentPage, itemsPerPage, activeFilters, sort, refreshKey]);
  
  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [status, debouncedQ, includeTimeline, itemsPerPage, activeFilters, sort]);

  useEffect(() => { localStorage.setItem("bc_columns", JSON.stringify(columns)); }, [columns]);

  const toggleCol = (key) => setColumns((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));

  const fields = [
    { key: "seriennummer", label: "Seriennummer" },
    { key: "beschreibung", label: "Beschreibung" },
    { key: "artikelnummer", label: "Artikel" },
    { key: "kunde", label: "Kunde" },
    { key: "status", label: "Status" },
    { key: "mietpreis_typ", label: "Zeitraum" },
    { key: "mietpreis_betrag", label: "Betrag" },
    { key: "hersteller", label: "Hersteller" },
    { key: "farbe_ral", label: "Farbe/RAL" },
    { key: "baujahr", label: "Baujahr" },
    { key: "cad_nummer", label: "CAD" },
  ];

  // Hilfsfunktionen für Mietpreis (Daten kommen jetzt vom Backend)
  const getMietpreisTyp = (becken) => {
    return becken.mietpreis_typ || "–";
  };

  const getMietpreisBetrag = (becken) => {
    if (becken.mietpreis_betrag !== null && becken.mietpreis_betrag !== undefined) {
      return `${becken.mietpreis_betrag.toFixed(2)} €`;
    }
    return "–";
  };

  const sorted = useMemo(() => {
    let arr = [...items];
    console.log('[Dashboard] sorted useMemo:', { itemsCount: arr.length, debouncedQ, includeTimeline });
    
    // WICHTIG: Bei aktiver Suche (debouncedQ) KEINE KPI-Filterung!
    const hasActiveSearch = debouncedQ && debouncedQ.length > 0;
    
    // Bei Suche OHNE Timeline-Toggle: 
    // Backend hat Items nach Hauptfeldern (Seriennummer, Artikelnummer, etc.) gefiltert
    // UND nach historischen Kunden gesucht (weil wir im Backend nicht zwischen aktuellem/historischem Kunden unterscheiden können)
    // Hier müssen wir client-seitig filtern: NUR Items anzeigen, die den Suchbegriff im AKTUELLEN Kunden haben
    if (hasActiveSearch && !includeTimeline && debouncedQ) {
      // Zweigeteilte Filterung:
      // 1. Items die in Hauptfeldern matchen (Seriennummer, Beschreibung, etc.) → BEHALTEN
      // 2. Items die NUR in historischen Kunden matchen → NUR behalten wenn aktueller Kunde auch matched
      arr = arr.filter(item => {
        const searchLower = debouncedQ.toLowerCase();
        
        // Prüfe ob Match in Hauptfeldern
        const hasMainFieldMatch = (
          (item.seriennummer && item.seriennummer.toLowerCase().includes(searchLower)) ||
          (item.beschreibung && item.beschreibung.toLowerCase().includes(searchLower)) ||
          (item.artikelnummer && item.artikelnummer.toLowerCase().includes(searchLower)) ||
          (item.hersteller && item.hersteller.toLowerCase().includes(searchLower)) ||
          (item.farbe_ral && item.farbe_ral.toLowerCase().includes(searchLower)) ||
          (item.cad_nummer && item.cad_nummer.toLowerCase().includes(searchLower)) ||
          (item.cloud && item.cloud.toLowerCase().includes(searchLower)) ||
          (item.modem && item.modem.toLowerCase().includes(searchLower))
        );
        
        // Wenn Hauptfeld-Match: IMMER behalten (egal ob Kunde matched)
        if (hasMainFieldMatch) {
          return true;
        }
        
        // Wenn kein Hauptfeld-Match: Nur behalten wenn aktueller Kunde matched
        const aktuellerKunde = item.aktueller_kunde || '';
        return aktuellerKunde.toLowerCase().includes(searchLower);
      });
      console.log('[Dashboard] Filtered for Timeline=OFF:', { filteredCount: arr.length, originalCount: items.length });
    }
    
    if (!hasActiveSearch) {
      // NUR bei INAKTIVER Suche: Client-seitige Filterung anwenden
      // KPI-Filter anwenden (sowohl Single als auch Multiple)
      // Backend liefert alle Items (limit=9999), wir filtern hier client-seitig
      if (activeFilters.length >= 1) {
        arr = arr.filter(item => {
          return activeFilters.some(filter => {
            // Spezial-Behandlung für Checkout-Filter
            if (filter.type === 'checkout') {
              if (filter.status === 'in_process') return item.is_checkout_in_process === true;
              if (filter.status === 'ready') return item.is_checkout_ready === true;
              return false;
            }
            // Normal-Filter für Becken/Anlagen
            return item.item_type === filter.type && item.status === filter.status;
          });
        });
      } else if (status === "used_online") {
        arr = arr.filter(b => b.has_online_expose === true);
      } else if (status === "checkout_in_process") {
        arr = arr.filter(b => b.is_checkout_in_process === true);
      } else if (status === "checkout_ready") {
        arr = arr.filter(b => b.is_checkout_ready === true);
      } else if (status !== "all") {
        arr = arr.filter(b => b.status === status);
      }
    }
    
    arr.sort((a, b) => {
      // Spezielle Behandlung für Seriennummern-Sortierung (natürliche/numerische Sortierung)
      if (sort.key === "seriennummer") {
        const serienA = a.seriennummer || "";
        const serienB = b.seriennummer || "";
        
        // Extrahiere numerischen Teil aus Seriennummern (z.B. "B-208005" → 208005)
        const extractNumber = (str) => {
          const match = String(str).match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };
        
        const numA = extractNumber(serienA);
        const numB = extractNumber(serienB);
        
        // Numerischer Vergleich
        const diff = numA - numB;
        // Standard-Sortierlogik: asc = aufsteigend (klein → groß), desc = absteigend (groß → klein)
        return sort.dir === "asc" ? diff : -diff;
      }
      
      // Spezielle Behandlung für Baujahr
      if (sort.key === "baujahr") {
        const yearA = a.baujahr_jahr ? a.baujahr_jahr * 100 + (a.baujahr_monat || 0) : 0;
        const yearB = b.baujahr_jahr ? b.baujahr_jahr * 100 + (b.baujahr_monat || 0) : 0;
        const diff = yearA - yearB;
        return sort.dir === "asc" ? diff : -diff;
      }
      
      // Standard-Sortierung für andere Felder
      const getVal = (x) => {
        const val = x[sort.key];
        // Für Strings: lowercase für case-insensitive Sort
        if (typeof val === 'string') return val.toLowerCase();
        // Für Zahlen und andere
        return val || "";
      };
      
      const va = getVal(a);
      const vb = getVal(b);
      
      let comparison = 0;
      if (va < vb) comparison = -1;
      else if (va > vb) comparison = 1;
      
      // Richtung anwenden
      return sort.dir === "asc" ? comparison : -comparison;
    });
    return arr;
  }, [items, sort, status, activeFilters, debouncedQ, includeTimeline]);
  // Berechne KPI-Stats nach client-seitiger Filterung neu
  const filteredKpiStats = useMemo(() => {
    // Wenn wir client-seitig gefiltert haben (Suche ohne Timeline), KPIs neu berechnen
    if (debouncedQ && !includeTimeline) {
      const stats = {
        becken_miete: 0,
        becken_kauf: 0,
        becken_bauhof: 0,
        becken_in_produktion: 0,
        anlagen_miete: 0,
        anlagen_kauf: 0,
        anlagen_bauhof: 0,
        anlagen_in_produktion: 0,
        checkout_in_process: 0,
        checkout_ready: 0
      };
      
      sorted.forEach(item => {
        const itemType = item.item_type;
        const itemStatus = item.status;
        
        if (itemType === 'becken') {
          if (itemStatus === 'miete') stats.becken_miete++;
          else if (itemStatus === 'kauf') stats.becken_kauf++;
          else if (itemStatus === 'bauhof') stats.becken_bauhof++;
          else if (itemStatus === 'in_produktion') stats.becken_in_produktion++;
        } else if (itemType === 'anlage') {
          if (itemStatus === 'miete') stats.anlagen_miete++;
          else if (itemStatus === 'kauf') stats.anlagen_kauf++;
          else if (itemStatus === 'bauhof') stats.anlagen_bauhof++;
          else if (itemStatus === 'in_produktion') stats.anlagen_in_produktion++;
        }
        
        if (item.is_checkout_in_process) stats.checkout_in_process++;
        if (item.is_checkout_ready) stats.checkout_ready++;
      });
      
      console.log('[Dashboard] Recalculated KPI stats after filtering:', stats);
      return stats;
    }
    
    // Sonst: Nutze Backend-Stats
    return kpiStats;
  }, [sorted, debouncedQ, includeTimeline, kpiStats]);

  // Berechnete Pagination-Werte (verwende sorted.length für Client-Pagination)
  const needsClientPagination = useMemo(() => {
    return activeFilters.length >= 1 || status === "used_online" || status === "checkout_in_process" || status === "checkout_ready" || (status === "all" && activeFilters.length === 0 && !debouncedQ);
  }, [activeFilters.length, status, debouncedQ]);

  const displayTotalItems = useMemo(() => {
    if (needsClientPagination) {
      return sorted.length;
    }
    return totalItems;
  }, [needsClientPagination, sorted.length, totalItems]);

  const displayTotalPages = useMemo(() => {
    if (needsClientPagination) {
      return Math.ceil(sorted.length / itemsPerPage);
    }
    return totalPages;
  }, [needsClientPagination, sorted.length, itemsPerPage, totalPages]);

  // Bei clientseitiger Filterung: Clientseitige Pagination der bereits gefilterten sorted Liste
  const displayedItems = useMemo(() => {
    if (needsClientPagination) {
      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return sorted.slice(start, end);
    }
    return sorted;
  }, [needsClientPagination, sorted, currentPage, itemsPerPage]);


  const toggleFilter = (itemType, statusValue) => {
    const filterKey = `${itemType}-${statusValue}`;
    const existingIndex = activeFilters.findIndex(f => `${f.type}-${f.status}` === filterKey);
    
    if (existingIndex >= 0) {
      // Filter entfernen
      setActiveFilters(activeFilters.filter((_, i) => i !== existingIndex));
    } else {
      // Filter hinzufügen
      setActiveFilters([...activeFilters, { type: itemType, status: statusValue }]);
    }
  };

  const isFilterActive = (itemType, statusValue) => {
    return activeFilters.some(f => f.type === itemType && f.status === statusValue);
  };

  const onSort = (key) => {
    if (sort.key === key) {
      setSort((s) => ({ ...s, dir: s.dir === "asc" ? "desc" : "asc" }));
    } else {
      setSort({ key, dir: "asc" });
    }
  };

  const exportCSV = async () => {
    // Hole ALLE Items mit aktuellem Filter (ohne Pagination)
    const params = new URLSearchParams();
    if (status && status !== "all" && status !== "used_online" && status !== "checkout_ready") params.set("status", status);
    if (debouncedQ) {
      params.set("q", debouncedQ);
      // Timeline-Toggle respektieren (User entscheidet ob historische oder nur aktuelle Kunden durchsucht werden)
      params.set("include_timeline", includeTimeline.toString());
    }
    
    // KPI-Filter: Wenn NUR EIN Filter aktiv ist, sende an Backend
    if (activeFilters.length === 1) {
      const filter = activeFilters[0];
      if (filter.type !== 'checkout') {
        params.set("item_type", filter.type);
        params.set("status", filter.status);
      } else if (filter.type === 'checkout') {
        if (filter.status === 'in_process') params.set("status", "checkout_in_process");
        if (filter.status === 'ready') params.set("status", "checkout_ready");
      }
    }
    
    // Hole alle Items (limit=9999)
    params.set("limit", "9999");
    
    try {
      const res = await api.get(`/items${params.toString() ? `?${params.toString()}` : ""}`);
      const allItems = res.data.items || [];
      
      // Wende Frontend-Filter an (used_online, checkout_ready, activeFilters)
      // Nur bei Mehrfachfiltern (>1) clientseitig filtern
      let filteredItems = [...allItems];
      if (activeFilters.length > 1) {
        filteredItems = filteredItems.filter(item => {
          return activeFilters.some(filter => {
            if (filter.type === 'checkout') {
              if (filter.status === 'in_process') return item.is_checkout_in_process === true;
              if (filter.status === 'ready') return item.is_checkout_ready === true;
              return false;
            }
            return item.item_type === filter.type && item.status === filter.status;
          });
        });
      } else if (status === "used_online") {
        filteredItems = filteredItems.filter(b => b.has_online_expose === true);
      } else if (status === "checkout_in_process") {
        filteredItems = filteredItems.filter(b => b.is_checkout_in_process === true);
      } else if (status === "checkout_ready") {
        filteredItems = filteredItems.filter(b => b.is_checkout_ready === true);
      }
      
      // CSV-Header
      const headers = ["Seriennummer", "Kunde", "Status", "Hersteller", "Farbe/RAL", "Baujahr", "Artikel", "CAD"];
      
      // CSV-Daten
      const rows = filteredItems.map(b => [
        b.seriennummer || "",
        b.aktueller_kunde || "",
        mapStatus(b.status),
        b.hersteller || "",
        b.farbe_ral || "",
        (b.baujahr_monat && b.baujahr_jahr) ? `${String(b.baujahr_monat).padStart(2,"0")}/${b.baujahr_jahr}` : "",
        b.artikelnummer || "",
        b.cad_nummer || ""
      ]);
      
      // CSV erstellen
      const csvContent = [
        headers.join(";"),
        ...rows.map(row => row.join(";"))
      ].join("\n");
      
      // Download
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `becken_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (err) {
      console.error("CSV Export Error:", err);
      alert("Fehler beim CSV-Export");
    }
  };

  const openCreateDialog = async () => {
    try {
      const data = await SettingsAPI.nextSerial();
      setNextSerial(data);
      
      try {
        const templatesRes = await api.get("/templates");
        setTemplates(templatesRes.data);
      } catch (err) {
        console.error("Fehler beim Laden der Templates:", err);
      }
      
      // Reset batch states
      setBatchAnzahl(1);
      setBatchBeschreibung("");
      setBatchBaujahrMonat("");
      setBatchBaujahrJahr(new Date().getFullYear().toString());
      setBatchFarbeRal("");
      setBatchAnschaffungspreis("");
      setBatchCreatedIds(null);
      setBatchCreating(false);
      setShowCreateDialog(true);
    } catch (err) {
      console.error("Fehler beim Abrufen der Seriennummer:", err);
      alert("Fehler beim Abrufen der Seriennummer!");
    }
  };

  // Felder die das Typenschild braucht
  const TYPENSCHILD_FIELDS = ["beschreibung", "baujahr_monat", "baujahr_jahr", "abmessungen", "farbe_ral", "gewicht", "inhalt", "cad_nummer", "seriennummer"];
  // Felder die ein Template liefern kann
  const TEMPLATE_PROVIDED = ["artikelnummer", "farbe_ral", "cad_nummer", "da_nummer", "doc_nummer", "abmessungen", "inhalt", "gewicht"];

  const getSelectedTemplate = () => {
    if (!selectedTemplate || selectedTemplate === "none") return null;
    return templates.find(t => t.id === selectedTemplate);
  };

  const getMissingTypenschildFields = () => {
    const tpl = getSelectedTemplate();
    const missing = [];
    // beschreibung - nie im Template
    missing.push("beschreibung");
    // baujahr - nie im Template
    missing.push("baujahr");
    // farbe_ral - nur wenn nicht im Template
    if (!tpl?.farbe_ral) missing.push("farbe_ral");
    return missing;
  };

  const createBecken = async () => {
    try {
      if (!nextSerial) return;
      setBatchCreating(true);

      if (batchAnzahl === 1 && (!selectedTemplate || selectedTemplate === "none")) {
        // Einzelnes Becken ohne Template (alter Ablauf)
        const beckenData = { 
          seriennummer: nextSerial.serial,
          hersteller: newHersteller
        };
        const res = await api.post("/becken", beckenData);
        await SettingsAPI.consume(nextSerial.next_number);
        setShowCreateDialog(false);
        setBatchCreating(false);
        setNewHersteller("Pirschel");
        setSelectedTemplate("none");
        nav(`/becken/${res.data.id}`);
        return;
      }

      // Batch-Erstellung
      const payload = {
        anzahl: batchAnzahl,
        hersteller: newHersteller,
        template_id: selectedTemplate !== "none" ? selectedTemplate : undefined,
      };
      if (batchBeschreibung) payload.beschreibung = batchBeschreibung;
      if (batchBaujahrMonat) payload.baujahr_monat = parseInt(batchBaujahrMonat);
      if (batchBaujahrJahr) payload.baujahr_jahr = parseInt(batchBaujahrJahr);
      const tpl = getSelectedTemplate();
      if (!tpl?.farbe_ral && batchFarbeRal) payload.farbe_ral = batchFarbeRal;
      if (batchAnschaffungspreis) payload.anschaffungspreis = parseFloat(batchAnschaffungspreis);

      const res = await api.post("/becken/batch", payload);
      setBatchCreatedIds(res.data.created);
      setBatchCreating(false);
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error("Fehler beim Erstellen:", err);
      alert("Fehler beim Erstellen der Becken!");
      setBatchCreating(false);
    }
  };

  const downloadBatchTypenschilder = async () => {
    try {
      const ids = batchCreatedIds.map(c => c.id);
      const res = await api.post("/becken/batch-typenschild", { ids }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Typenschilder_Batch_${ids.length}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Fehler beim Download:", err);
      alert("Fehler beim Herunterladen der Typenschilder!");
    }
  };

  const openCreateAnlageDialog = async () => {
    try {
      const res = await api.get("/settings/next_anlagen_serial");
      setNextAnlagenSerial(res.data);
      setShowCreateAnlageDialog(true);
    } catch (err) {
      console.error("Fehler beim Abrufen der Anlagen-Seriennummer:", err);
      alert("Fehler beim Abrufen der Seriennummer!");
    }
  };

  const createAnlage = async () => {
    try {
      if (!nextAnlagenSerial) return;
      
      const anlagenData = { 
        seriennummer: nextAnlagenSerial.serial,
        hersteller: "Möbus Umweltschutz GmbH"
      };
      
      const res = await api.post("/anlagen", anlagenData);
      await api.post("/settings/consume_anlagen_next", { used_number: nextAnlagenSerial.next_number });
      setShowCreateAnlageDialog(false);
      nav(`/anlagen/${res.data.id}`);
    } catch (err) {
      console.error("Fehler beim Erstellen:", err);
      alert("Fehler beim Erstellen der Anlage!");
    }
  };


  return (
    <div className="space-y-4">
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { if (!batchCreatedIds) setShowCreateDialog(false); }}>
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            
            {/* Erfolgs-Ansicht nach Batch-Erstellung */}
            {batchCreatedIds ? (
              <>
                <h3 className="text-lg font-semibold mb-3">{batchCreatedIds.length} Becken angelegt</h3>
                <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                  {batchCreatedIds.map((c, i) => (
                    <div key={c.id} className="flex items-center justify-between text-sm py-1 px-2 bg-neutral-50 rounded">
                      <span className="font-medium">{c.seriennummer}</span>
                      <button 
                        className="text-blue-600 hover:underline text-xs"
                        onClick={() => { setShowCreateDialog(false); nav(`/becken/${c.id}`); }}
                      >
                        Öffnen
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowCreateDialog(false); setBatchCreatedIds(null); }}
                  >
                    Schließen
                  </Button>
                  <Button 
                    onClick={downloadBatchTypenschilder}
                    data-testid="batch-typenschild-download-btn"
                  >
                    <Download size={14} className="mr-1" />
                    {batchCreatedIds.length} Typenschilder herunterladen
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-4">Becken anlegen</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="text-sm text-neutral-600">Nächste Seriennummer</label>
                    <div className="text-lg font-semibold text-neutral-900">{nextSerial?.serial}</div>
                  </div>

                  {/* Anzahl */}
                  <div>
                    <label className="text-sm text-neutral-600 block mb-1">Anzahl</label>
                    <Input 
                      type="number" 
                      min={1} 
                      max={50} 
                      value={batchAnzahl} 
                      onChange={(e) => setBatchAnzahl(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} 
                      className="w-24"
                      data-testid="batch-anzahl-input"
                    />
                    {batchAnzahl > 1 && (
                      <div className="text-xs text-neutral-500 mt-1">
                        Seriennummern: {nextSerial?.serial} bis {nextSerial?.prefix}{nextSerial?.next_number + batchAnzahl - 1}
                      </div>
                    )}
                  </div>

                  {/* Hersteller */}
                  <div>
                    <label className="text-sm text-neutral-600 block mb-1">Hersteller</label>
                    <Select value={newHersteller} onValueChange={setNewHersteller}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pirschel">Pirschel</SelectItem>
                        <SelectItem value="AMG Polen">AMG Polen</SelectItem>
                        <SelectItem value="Moebus">Moebus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Template */}
                  {templates.length > 0 && (
                    <div>
                      <label className="text-sm text-neutral-600 block mb-1">Template</label>
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger>
                          <SelectValue placeholder="Kein Template" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Kein Template</SelectItem>
                          {templates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedTemplate && selectedTemplate !== "none" && (() => {
                        const t = templates.find(x => x.id === selectedTemplate);
                        return t ? (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs space-y-1">
                            <div className="font-medium">Wird aus Template übernommen:</div>
                            {t.artikelnummer && <div>Artikel: {t.artikelnummer}</div>}
                            {t.farbe_ral && <div>Farbe: {t.farbe_ral}</div>}
                            {t.cad_nummer && <div>CAD: {t.cad_nummer}</div>}
                            {t.da_nummer && <div>DA: {t.da_nummer}</div>}
                            {t.doc_nummer && <div>DOC: {t.doc_nummer}</div>}
                            {t.abmessungen && <div>Abmessungen: {t.abmessungen}</div>}
                            {t.gewicht && <div>Gewicht: {t.gewicht}</div>}
                            {t.inhalt && <div>Volumen: {t.inhalt}</div>}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Zusätzliche Felder für Typenschild (nur bei Template-Auswahl) */}
                  {selectedTemplate && selectedTemplate !== "none" && (
                    <div className="border-t pt-3">
                      <label className="text-xs font-medium text-neutral-500 block mb-2">
                        Zusätzliche Felder für Typenschild
                      </label>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-neutral-600 block mb-1">Beschreibung</label>
                          <Input 
                            value={batchBeschreibung} 
                            onChange={(e) => setBatchBeschreibung(e.target.value)}
                            placeholder="z.B. Absetzbecken 3.800"
                            data-testid="batch-beschreibung-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm text-neutral-600 block mb-1">Baujahr Monat</label>
                            <Select value={batchBaujahrMonat} onValueChange={setBatchBaujahrMonat}>
                              <SelectTrigger>
                                <SelectValue placeholder="Monat" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({length: 12}, (_, i) => (
                                  <SelectItem key={i+1} value={String(i+1)}>
                                    {String(i+1).padStart(2, "0")}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm text-neutral-600 block mb-1">Baujahr Jahr</label>
                            <Input 
                              type="number" 
                              value={batchBaujahrJahr} 
                              onChange={(e) => setBatchBaujahrJahr(e.target.value)}
                              placeholder="2026"
                              data-testid="batch-baujahr-jahr-input"
                            />
                          </div>
                        </div>
                        {/* Farbe nur wenn nicht im Template */}
                        {!getSelectedTemplate()?.farbe_ral && (
                          <div>
                            <label className="text-sm text-neutral-600 block mb-1">Farbe/RAL</label>
                            <Input 
                              value={batchFarbeRal} 
                              onChange={(e) => setBatchFarbeRal(e.target.value)}
                              placeholder="z.B. RAL 5010"
                              data-testid="batch-farbe-input"
                            />
                          </div>
                        )}
                        <div>
                          <label className="text-sm text-neutral-600 block mb-1">Anschaffungspreis (netto, EUR)</label>
                          <Input 
                            type="number"
                            value={batchAnschaffungspreis} 
                            onChange={(e) => setBatchAnschaffungspreis(e.target.value)}
                            placeholder="z.B. 4500"
                            data-testid="batch-anschaffungspreis-input"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
                  <Button onClick={createBecken} disabled={batchCreating} data-testid="batch-create-btn">
                    {batchCreating ? "Wird erstellt..." : batchAnzahl > 1 ? `${batchAnzahl} Becken anlegen` : "Anlegen"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}


      {/* Anlage erstellen Dialog */}
      {showCreateAnlageDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateAnlageDialog(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Neue Anlage anlegen</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm text-neutral-600">Seriennummer</label>
                <div className="text-lg font-semibold text-neutral-900">{nextAnlagenSerial?.serial}</div>
              </div>
              <div>
                <label className="text-sm text-neutral-600">Hersteller</label>
                <div className="text-sm text-neutral-700">Möbus Umweltschutz GmbH</div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreateAnlageDialog(false)}>Abbrechen</Button>
              <Button onClick={createAnlage}>Anlegen</Button>
            </div>
          </div>
        </div>
      )}


      {/* Spaltenauswahl Dialog */}
      {showColumnsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowColumnsDialog(false)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Spalten auswählen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {fields.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-neutral-50 p-2 rounded">
                  <Checkbox checked={columns.includes(f.key)} onCheckedChange={() => toggleCol(f.key)} />
                  <span>{f.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between items-center">
              <Button 
                variant="outline" 
                onClick={() => setColumns(DEFAULT_COLUMNS)}
              >
                Standard wiederherstellen
              </Button>
              <Button onClick={() => setShowColumnsDialog(false)}>Fertig</Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <div className="space-y-3 flex-1">
          {(activeFilters.length > 0 || q) && (
            <div 
              className="flex items-center justify-between px-4 py-2 bg-red-50 border border-red-200 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
              onClick={() => { setActiveFilters([]); setQ(""); }}
              title="Klicken um Filter zurückzusetzen"
            >
              <span className="text-sm font-medium text-red-700">
                {[activeFilters.length > 0 && `${activeFilters.length} Filter`, q && `Suche: "${q}"`].filter(Boolean).join(" + ")} aktiv
              </span>
              <span className="text-sm text-red-600">
                Alle anzeigen
              </span>
            </div>
          )}
          {/* Oberste Reihe: Anlagen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI 
              type="Anlagen"
              status="Vermietet"
              value={filteredKpiStats?.anlagen_miete || 0} 
              bgColor="bg-green-100" 
              textColor="text-green-700" 
              onClick={() => toggleFilter('anlage', 'miete')}
              isActive={isFilterActive('anlage', 'miete')}
            />
            <KPI 
              type="Anlagen"
              status="Verkauft"
              value={filteredKpiStats?.anlagen_kauf || 0} 
              bgColor="bg-green-100" 
              textColor="text-green-700" 
              onClick={() => toggleFilter('anlage', 'kauf')}
              isActive={isFilterActive('anlage', 'kauf')}
            />
            <KPI 
              type="Anlagen"
              status="Bauhof"
              value={filteredKpiStats?.anlagen_bauhof || 0} 
              bgColor="bg-yellow-100" 
              textColor="text-yellow-700" 
              onClick={() => toggleFilter('anlage', 'bauhof')}
              isActive={isFilterActive('anlage', 'bauhof')}
            />
            <KPI 
              type="Anlagen"
              status="Produktion"
              value={filteredKpiStats?.anlagen_produktion || 0} 
              bgColor="bg-blue-100" 
              textColor="text-blue-700" 
              onClick={() => toggleFilter('anlage', 'in_produktion')}
              isActive={isFilterActive('anlage', 'in_produktion')}
            />
            {/* Leere Zelle für gleiche Breite */}
            <div></div>
          </div>
          
          {/* Unterste Reihe: Becken + Checkout Status */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <KPI 
              type="Becken"
              status="Vermietet"
              value={filteredKpiStats?.becken_miete || 0} 
              bgColor="bg-green-100" 
              textColor="text-green-700" 
              onClick={() => toggleFilter('becken', 'miete')}
              isActive={isFilterActive('becken', 'miete')}
            />
            <KPI 
              type="Becken"
              status="Verkauft"
              value={filteredKpiStats?.becken_kauf || 0} 
              bgColor="bg-green-100" 
              textColor="text-green-700" 
              onClick={() => toggleFilter('becken', 'kauf')}
              isActive={isFilterActive('becken', 'kauf')}
            />
            <KPI 
              type="Becken"
              status="Bauhof"
              value={filteredKpiStats?.becken_bauhof || 0} 
              bgColor="bg-yellow-100" 
              textColor="text-yellow-700" 
              onClick={() => toggleFilter('becken', 'bauhof')}
              isActive={isFilterActive('becken', 'bauhof')}
            />
            <KPI 
              type="Becken"
              status="Produktion"
              value={filteredKpiStats?.becken_produktion || 0} 
              bgColor="bg-blue-100" 
              textColor="text-blue-700" 
              onClick={() => toggleFilter('becken', 'in_produktion')}
              isActive={isFilterActive('becken', 'in_produktion')}
            />
            <KPI 
              type="Checkout"
              status="In Process"
              value={filteredKpiStats?.checkout_in_process || 0} 
              bgColor="bg-purple-100" 
              textColor="text-purple-700" 
              onClick={() => toggleFilter('checkout', 'in_process')}
              isActive={isFilterActive('checkout', 'in_process')}
            />
            <KPI 
              type="Checkout"
              status="Ready"
              value={filteredKpiStats?.checkout_ready || 0} 
              bgColor="bg-purple-100" 
              textColor="text-purple-700" 
              onClick={() => toggleFilter('checkout', 'ready')}
              isActive={isFilterActive('checkout', 'ready')}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 justify-start">
          <Button onClick={openCreateDialog}>Neues Becken</Button>
          <Button variant="outline" onClick={openCreateAnlageDialog}>Neue Anlage</Button>
          <Button variant="outline" onClick={exportCSV}>
            <Download size={16} className="mr-2" />
            CSV Export
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="text-sm">Freitextsuche</label>
          <div className="flex gap-2 items-center">
            <Input placeholder="Seriennummer, Hersteller, aktueller Kunde…" value={q} onChange={(e) => setQ(e.target.value)} />
            <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer">
              <Checkbox checked={includeTimeline} onCheckedChange={setIncludeTimeline} />
              incl. Timeline
            </label>
          </div>
        </div>
        <div className="w-full sm:w-60">
          <label className="text-sm">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Alle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="miete">Vermietet</SelectItem>
              <SelectItem value="kauf">Verkauft</SelectItem>
              <SelectItem value="bauhof">Bauhof</SelectItem>
              <SelectItem value="in_produktion">In Produktion</SelectItem>
              <SelectItem value="checkout_in_process">Checkout in Process</SelectItem>
              <SelectItem value="checkout_ready">Checkout Ready</SelectItem>
              <SelectItem value="used_online">Used online aktiv</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-auto">
          <label className="text-sm">Spalten</label>
          <Button variant="outline" onClick={() => setShowColumnsDialog(true)} className="w-full">
            Spalten auswählen ({columns.length})
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {/* Skeleton for table rows */}
          {[...Array(10)].map((_, i) => (
            <div key={i} className="flex gap-4 animate-pulse">
              <div className="h-12 bg-gray-200 rounded flex-1"></div>
              <div className="h-12 bg-gray-200 rounded w-32"></div>
              <div className="h-12 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.includes("seriennummer") && <SortableHead colKey="seriennummer" label="Seriennummer" sort={sort} onSort={onSort} />}
                {columns.includes("beschreibung") && <SortableHead colKey="beschreibung" label="Beschreibung" sort={sort} onSort={onSort} />}
                {columns.includes("artikelnummer") && <SortableHead colKey="artikelnummer" label="Artikel" sort={sort} onSort={onSort} />}
                {columns.includes("kunde") && <SortableHead colKey="kunde" label="Kunde" sort={sort} onSort={onSort} />}
                {columns.includes("status") && <SortableHead colKey="status" label="Status" sort={sort} onSort={onSort} />}
                {columns.includes("mietpreis_typ") && <TableHead>Zeitraum</TableHead>}
                {columns.includes("mietpreis_betrag") && <TableHead className="min-w-[100px]">Betrag</TableHead>}
                {columns.includes("hersteller") && <SortableHead colKey="hersteller" label="Hersteller" sort={sort} onSort={onSort} />}
                {columns.includes("farbe_ral") && <SortableHead colKey="farbe_ral" label="Farbe/RAL" sort={sort} onSort={onSort} />}
                {columns.includes("baujahr") && <SortableHead colKey="baujahr" label="Baujahr" sort={sort} onSort={onSort} />}
                {columns.includes("cad_nummer") && <SortableHead colKey="cad_nummer" label="CAD" sort={sort} onSort={onSort} />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedItems.map((b) => (
                <TableRow key={b.id} className="cursor-pointer hover:bg-neutral-50" onClick={() => nav(b.item_type === 'becken' ? `/becken/${b.id}` : `/anlagen/${b.id}`)}>
                  {columns.includes("seriennummer") && <TableCell className="font-medium">{b.seriennummer}</TableCell>}
                  {columns.includes("beschreibung") && <TableCell>{b.beschreibung || "–"}</TableCell>}
                  {columns.includes("artikelnummer") && <TableCell>{b.artikelnummer || "–"}</TableCell>}
                  {columns.includes("kunde") && <TableCell>{b.aktueller_kunde || "–"}</TableCell>}
                  {columns.includes("status") && (
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <StatusWithIcon status={b.status} bauhof_status={b.bauhof_status} abweichender_standort={b.abweichender_standort} abweichende_adresse={b.abweichende_adresse} />
                          {b.has_online_expose && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              Used Online aktiv
                            </Badge>
                          )}
                        </div>
                        {b.is_checkout_in_process && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs font-semibold w-fit">
                            🔄 Checkout in Process
                          </Badge>
                        )}
                        {b.is_checkout_ready && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs font-semibold w-fit">
                            ✅ Checkout Ready
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  )}
                  {columns.includes("mietpreis_typ") && <TableCell>{getMietpreisTyp(b)}</TableCell>}
                  {columns.includes("mietpreis_betrag") && <TableCell className="whitespace-nowrap preis">{getMietpreisBetrag(b)}</TableCell>}
                  {columns.includes("hersteller") && <TableCell>{b.hersteller || "–"}</TableCell>}
                  {columns.includes("farbe_ral") && <TableCell>{b.farbe_ral || "–"}</TableCell>}
                  {columns.includes("baujahr") && <TableCell>{(b.baujahr_monat && b.baujahr_jahr) ? `${String(b.baujahr_monat).padStart(2,"0")}/${b.baujahr_jahr}` : "–"}</TableCell>}
                  {columns.includes("cad_nummer") && (
                    <TableCell>
                      {b.cad_nummer ? (
                        <a
                          href={`/media-verwaltung?tab=design&design=${encodeURIComponent(b.cad_nummer)}`}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); nav(`/media-verwaltung?tab=design&design=${encodeURIComponent(b.cad_nummer)}`); }}
                          className="font-mono text-blue-600 hover:text-blue-800 hover:underline"
                          title="Zeichnung in der Design-Bibliothek öffnen"
                          data-testid={`becken-cad-link-${b.id}`}
                        >
                          {b.cad_nummer}
                        </a>
                      ) : "–"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      {/* Pagination Controls */}
      {!loading && displayedItems.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600">Einträge pro Seite:</span>
            <Select value={itemsPerPage.toString()} onValueChange={(val) => setItemsPerPage(parseInt(val))}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-neutral-600">
              Zeige {((currentPage - 1) * itemsPerPage) + 1} bis {Math.min(currentPage * itemsPerPage, displayTotalItems)} von {displayTotalItems}
            </span>
          </div>
          
          {/* Page numbers */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ← Vorherige
            </Button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: displayTotalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page, and pages around current
                const showPage = 
                  page === 1 || 
                  page === displayTotalPages || 
                  (page >= currentPage - 2 && page <= currentPage + 2);
                
                const showEllipsis = 
                  (page === currentPage - 3 && currentPage > 4) ||
                  (page === currentPage + 3 && currentPage < displayTotalPages - 3);
                
                if (showEllipsis) {
                  return <span key={page} className="px-2 text-neutral-400">...</span>;
                }
                
                if (!showPage) return null;
                
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className="min-w-[40px]"
                  >
                    {page}
                  </Button>
                );
              })}
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(displayTotalPages, p + 1))}
              disabled={currentPage === displayTotalPages}
            >
              Nächste →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, bgColor = "bg-white/80", textColor = "text-neutral-700", onClick, isActive = false, type, status }) {
  return (
    <div 
      className={`p-3 rounded-lg border shadow-sm ${bgColor} cursor-pointer hover:opacity-80 transition-all active:scale-95 ${
        isActive ? 'ring-2 ring-offset-2 ring-neutral-900 shadow-lg' : ''
      } flex flex-col items-center justify-center text-center min-h-[100px]`}
      onClick={onClick}
    >
      <div className="text-lg font-bold">{type}</div>
      <div className="text-sm font-medium opacity-80">{status}</div>
      <div className={`text-2xl font-semibold ${textColor} mt-1`}>{value}</div>
    </div>
  );
}

function mapStatus(s) {
  switch (s) {
    case "miete": return "Miete";
    case "kauf": return "Kauf";
    case "bauhof": return "Bauhof";
    case "in_produktion": return "In Produktion";
    default: return s;
  }
}

// Memoized components for better performance
const SortableHead = React.memo(({ colKey, label, sort, onSort }) => {
  const isActive = sort.key === colKey;
  return (
    <TableHead 
      onClick={() => onSort(colKey)} 
      className="cursor-pointer select-none hover:bg-neutral-100"
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isActive && (
          sort.dir === "asc" ? <ArrowUp size={14} /> : <ArrowDown size={14} />
        )}
      </div>
    </TableHead>
  );
});

const StatusWithIcon = React.memo(({ status, bauhof_status, abweichender_standort, abweichende_adresse }) => {
  const getBauhofSubBadge = (bauhofStatus) => {
    if (!bauhofStatus) return null;
    
    const subBadges = {
      wartend: { icon: '🕐', label: 'Wartend', className: 'bg-yellow-200 text-yellow-900' },
      gesperrt: { icon: '❌', label: 'Gesperrt', className: 'bg-red-200 text-red-900' },
      fertig: { icon: '✓', label: 'Fertig', className: 'bg-green-200 text-green-900' },
      reserviert: { icon: '🌊', label: 'Reserviert', className: 'bg-blue-200 text-blue-900' }
    };
    
    const badge = subBadges[bauhofStatus];
    if (!badge) return null;
    
    return (
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ml-1 ${badge.className}`}>
        <span>{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    );
  };
  
  switch (status) {
    case "miete":
      return <Badge className="bg-green-100 text-green-700 font-semibold text-xs px-3 py-1">Vermietet</Badge>;
    case "kauf":
      return <Badge className="bg-green-100 text-green-700 font-semibold text-xs px-3 py-1">Verkauft</Badge>;
    case "bauhof":
      return (
        <span className="inline-flex items-center flex-wrap gap-1">
          <Badge className="bg-yellow-100 text-yellow-700 font-semibold text-xs px-3 py-1">Bauhof</Badge>
          {getBauhofSubBadge(bauhof_status)}
          {abweichender_standort && (
            <Badge 
              className="bg-orange-100 text-orange-700 font-semibold text-xs px-2 py-1 cursor-help"
              title={abweichende_adresse || "Abweichender Standort"}
            >
              📍 Abw. Standort
            </Badge>
          )}
        </span>
      );
    case "in_produktion":
      return <Badge className="bg-blue-100 text-blue-700 font-semibold text-xs px-3 py-1">In Produktion</Badge>;
    case "entsorgt":
      return <Badge className="bg-red-100 text-red-700 font-semibold text-xs px-3 py-1">Entsorgt</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-700 font-semibold text-xs px-3 py-1">{mapStatus(status)}</Badge>;
  }
});
